import { useState, useEffect, useCallback, useMemo } from "react";
import { useShow } from "@/contexts/ShowContext";
import { useModal } from "@/contexts/ModalContext";
import { useWebSocket } from "@/hooks/useWebSocket";
import useKagamiBridge from "@/hooks/useKagamiBridge";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";

import Loader from "../loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/shadcn/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/shadcn/dropdown-menu";
import TaskModal from "../modals/TaskModal";
import ConfirmModal from "../modals/ConfirmModal";

import {
  FiCheck,
  FiAlertCircle,
  FiClock,
  FiTrash2,
  FiMoreHorizontal,
  FiChevronDown,
  FiCpu,
  FiActivity,
} from "react-icons/fi";
import {
  FaCog,
  FaTasks,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
} from "react-icons/fa";
import { TbCancel } from "react-icons/tb";
import { IoIosRefresh } from "react-icons/io";

const PAGE_SIZE = 50;

const WIDTH_STORAGE_KEY = "kagami-tasks-sidebar-width";
const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 280;
const MAX_WIDTH = 1000;

// User-facing status filter groups. "In Progress" groups several underlying states.
const STATE_GROUPS = [
  {
    value: "in_progress",
    label: "In Progress",
    states: ["scheduled", "pending", "in_progress"],
  },
  { value: "completed", label: "Completed", states: ["completed"] },
  { value: "failed", label: "Failed", states: ["failed"] },
];

function StateBadge({ state }) {
  const map = {
    cancelled: {
      icon: <TbCancel className="w-4 h-4 text-gray-500" />,
      label: "Cancelled",
    },
    scheduled: {
      icon: <FiClock className="w-4 h-4 text-gray-400" />,
      label: "Scheduled",
    },
    completed: {
      icon: <FiCheck className="w-4 h-4 text-green-500" />,
      label: "Completed",
    },
    failed: {
      icon: <FiAlertCircle className="w-4 h-4 text-red-500" />,
      label: "Failed",
    },
    in_progress: {
      icon: (
        <FaCog className="w-4 h-4 text-secondary-foreground/80 animate-spin" />
      ),
      label: "In progress",
    },
    pending: {
      icon: (
        <FaCog className="w-4 h-4 text-secondary-foreground/80 animate-spin" />
      ),
      label: "Pending",
    },
  };
  const entry = map[state] || { icon: null, label: state };
  return (
    <span
      className="flex items-center text-secondary-foreground"
      title={entry.label}>
      {entry.icon}
    </span>
  );
}

// Own-tasks-only task list + pagination, driven from a single hook so the
// collapsed toggle button (badges) and the expanded panel (table) can live
// in different parts of the AssetsEditor layout while sharing one fetch loop.
export function useTasksSidebar(aiInputRef) {
  const { show } = useShow();
  const { openModal } = useModal();
  const { cancelTask, deleteTask } = useKagamiBridge();

  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_WIDTH;
    const saved = parseInt(localStorage.getItem(WIDTH_STORAGE_KEY), 10);
    return Number.isFinite(saved)
      ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, saved))
      : DEFAULT_WIDTH;
  });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  // Model filter options + selection. `selectedModelIds` starts `null`
  // (meaning "not yet initialized") and is seeded to "every model" once the
  // list loads, mirroring AssetsEditor's media-type filter.
  const [models, setModels] = useState([]);
  const [selectedModelIds, setSelectedModelIds] = useState(null);
  const [selectedStates, setSelectedStates] = useState(
    STATE_GROUPS.map((g) => g.value),
  );

  const allModelsSelected =
    selectedModelIds == null || selectedModelIds.length === models.length;
  const allStatesSelected = selectedStates.length === STATE_GROUPS.length;

  useEffect(() => {
    let cancelled = false;
    apiFetch("/api/models")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (cancelled) return;
        setModels(data);
        setSelectedModelIds(data.map((m) => m._id));
      })
      .catch((error) => console.error("Error fetching models:", error));
    return () => {
      cancelled = true;
    };
  }, []);

  const showId = show?._id;

  // No `scope=all` is ever sent here — the backend already restricts
  // /api/tasks to the requesting user's own tasks unless a manager opts
  // into scope=all, so this always reflects the active user only.
  const fetchTasks = useCallback(
    async ({ silent = false } = {}) => {
      if (!showId) return;
      try {
        if (!silent) setLoading(true);
        const params = new URLSearchParams();
        params.set("show", showId);
        params.set("page", String(page));
        params.set("size", String(PAGE_SIZE));
        if (!allStatesSelected) {
          const states = STATE_GROUPS.filter((g) =>
            selectedStates.includes(g.value),
          ).flatMap((g) => g.states);
          params.set("state", states.length ? states.join(",") : "none");
        }
        if (!allModelsSelected) {
          params.set(
            "model",
            selectedModelIds.length ? selectedModelIds.join(",") : "none",
          );
        }

        const response = await apiFetch(`/api/tasks?${params.toString()}`);
        if (!response.ok) throw new Error("Failed to fetch tasks");
        const data = await response.json();
        setTasks(data.tasks || []);
        setTotalPages(data.totalPages || 0);
        setTotal(data.total || 0);
        setActiveCount(data.active || 0);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [
      showId,
      page,
      selectedStates,
      allStatesSelected,
      selectedModelIds,
      allModelsSelected,
    ],
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const toggleState = useCallback((value, checked) => {
    setPage(1);
    setSelectedStates((prev) =>
      checked
        ? Array.from(new Set([...prev, value]))
        : prev.filter((v) => v !== value),
    );
  }, []);

  const toggleModel = useCallback((modelId, checked) => {
    setPage(1);
    setSelectedModelIds((prev) =>
      checked
        ? Array.from(new Set([...(prev || []), modelId]))
        : (prev || []).filter((id) => id !== modelId),
    );
  }, []);

  useWebSocket(
    ["task_updated", "task_completed", "task_failed", "task_cancelled"],
    {
      onMessage: (message) => {
        fetchTasks({ silent: true });
        if (message?.event === "task_failed" && !open) {
          setHasError(true);
        }
      },
    },
  );

  // Expanding the sidebar (or a plain page refresh, which resets this
  // state entirely) both clear the error badge.
  useEffect(() => {
    if (open) setHasError(false);
  }, [open]);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Divider is dragged from the left edge of the (right-anchored) panel, so
  // moving the mouse left grows the sidebar and moving it right shrinks it.
  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth =
      e.currentTarget.parentElement?.getBoundingClientRect().width ??
      DEFAULT_WIDTH;
    const maxWidth = Math.min(MAX_WIDTH, window.innerWidth - 400);

    let latest = startWidth;
    const onMove = (ev) => {
      latest = Math.min(
        maxWidth,
        Math.max(MIN_WIDTH, startWidth + (startX - ev.clientX)),
      );
      setWidth(latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try {
        localStorage.setItem(WIDTH_STORAGE_KEY, String(latest));
      } catch {
        /* ignore */
      }
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const handleCancel = async (task) => {
    const confirmed = await openModal(
      <ConfirmModal
        title="Cancel Task"
        message="Are you sure you want to cancel this task?"
      />,
    );
    if (!confirmed) return;
    try {
      setActioningId(task._id);
      await cancelTask(task._id);
      toast.success("Task cancelled", {
        duration: 4000,
        position: "top-center",
      });
      await fetchTasks({ silent: true });
    } catch (error) {
      toast.error("Failed to cancel task", {
        description: error.message,
        duration: 5000,
        position: "top-center",
      });
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (task) => {
    const confirmed = await openModal(
      <ConfirmModal
        title="Delete Task"
        message="Are you sure you want to delete this task?"
      />,
    );
    if (!confirmed) return;
    try {
      setActioningId(task._id);
      await deleteTask(task._id);
      toast.success("Task deleted", { duration: 4000, position: "top-center" });
      await fetchTasks({ silent: true });
    } catch (error) {
      toast.error("Failed to delete task", {
        description: error.message,
        duration: 5000,
        position: "top-center",
      });
    } finally {
      setActioningId(null);
    }
  };

  // Preset the floating AiModelInput from the task's own model + payload —
  // the same recall mechanism AssetsEditor's `handleRecallSettings` uses
  // for an asset's recorded generation (see `asset.generation` there).
  const handleRecallSettings = useCallback(
    (task) => {
      const modelId = task.model?._id || task.model;
      const settings = task.request_body?.payload;
      if (!modelId || !settings) return;
      aiInputRef?.current?.recallSettings({ modelId, settings });
    },
    [aiInputRef],
  );

  const startIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, total);

  return {
    open,
    toggle,
    width,
    startResize,
    tasks,
    loading,
    page,
    setPage,
    totalPages,
    total,
    activeCount,
    hasError,
    actioningId,
    handleCancel,
    handleDelete,
    handleRecallSettings,
    startIndex,
    endIndex,
    models,
    selectedModelIds,
    allModelsSelected,
    setSelectedModelIds,
    toggleModel,
    selectedStates,
    allStatesSelected,
    setSelectedStates,
    toggleState,
    // Exposed so a freshly-submitted task (which the backend doesn't
    // announce over the websocket until a worker picks it up) can be
    // reflected right away instead of waiting on that first worker event.
    refresh: () => fetchTasks({ silent: true }),
  };
}

// Toggle button meant to sit in AssetsEditor's top control bar, right after
// the zoom in/out controls. Badges only render while the panel is collapsed.
export function TasksSidebarToggle({ sidebar }) {
  const { open, toggle, activeCount, hasError } = sidebar;
  return (
    <button
      type="button"
      onClick={toggle}
      title="Tasks"
      className={`relative inline-flex h-9 w-9 outline-none items-center justify-center rounded-md border cursor-pointer ${open ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary text-secondary-foreground"}`}>
      {!open && hasError ? (
        <span className="w-full h-full bg-red-500 text-white flex items-center justify-center animate-pulse rounded-md">
          <FiAlertCircle className="w-5 h-5" />
        </span>
      ) : (
        <FaTasks className="w-4 h-4" />
      )}
      {!open && activeCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 text-[10px] min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          {activeCount > 99 ? "99+" : activeCount}
        </span>
      )}
    </button>
  );
}

// Collapsible right-hand panel: a responsive (horizontally-scrollable) table
// of the active user's tasks, with pagination pinned to the bottom.
export default function TasksSidebarPanel({ sidebar }) {
  const { openModal } = useModal();
  const {
    open,
    width,
    startResize,
    tasks,
    loading,
    page,
    setPage,
    totalPages,
    total,
    handleCancel,
    handleDelete,
    handleRecallSettings,
    actioningId,
    startIndex,
    endIndex,
    models,
    selectedModelIds,
    allModelsSelected,
    setSelectedModelIds,
    toggleModel,
    selectedStates,
    allStatesSelected,
    setSelectedStates,
    toggleState,
  } = sidebar;

  // Grouped by the model's `group` field, groups and models both sorted by name.
  const groupedModels = useMemo(() => {
    const groups = new Map();
    for (const model of models) {
      const key = model.group || "Other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(model);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, list]) => [
        group,
        list.slice().sort((a, b) => a.name.localeCompare(b.name)),
      ]);
  }, [models]);

  if (!open) return null;

  return (
    <aside
      className="relative shrink-0 h-full border-l bg-background flex flex-col overflow-hidden @container"
      style={{ width }}>
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        className="absolute left-0 top-0 h-full w-1.5 -translate-x-1/2 cursor-col-resize z-20"
      />
      <div className="flex items-center px-4 h-20 shrink-0">
        <h1 className="text-2xl font-bold">Tasks</h1>
      </div>

      {/* Filter toolbar — Model / Status dropdowns on the left, pagination pinned right. */}
      <div className="flex items-center gap-1 px-2 pb-2 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
              <FiCpu className="w-4 h-4 @[460px]:hidden" />
              <span className="@[460px]:block hidden">Model</span>
              {!allModelsSelected ? (
                <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none">
                  {(selectedModelIds || []).length}
                </span>
              ) : (
                <FiChevronDown className="w-4 h-4 text-secondary-foreground/60 @[460px]:block hidden" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-max max-h-[500px] overflow-y-auto">
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer"
              onClick={() => {
                setPage(1);
                setSelectedModelIds(models.map((m) => m._id));
              }}>
              Select all
            </button>
            {groupedModels.map(([group, list], i) => (
              <div key={group}>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="px-2 py-1 text-[10px] uppercase tracking-wide text-secondary-foreground/50">
                  {group}
                </DropdownMenuLabel>
                {list.map((model) => (
                  <DropdownMenuCheckboxItem
                    key={model._id}
                    checked={(selectedModelIds || []).includes(model._id)}
                    onCheckedChange={(checked) =>
                      toggleModel(model._id, checked)
                    }
                    onSelect={(e) => e.preventDefault()}
                    className="group text-xs h-6">
                    <span className="flex-1 whitespace-nowrap pr-2">
                      {model.name}
                    </span>
                    {/* `invisible` (not `hidden`) so this still occupies its layout
                                            space when not hovered — otherwise the dropdown's `w-max` width
                                            is computed without it, and the panel grows/shifts on hover. */}
                    <button
                      type="button"
                      className="ml-auto invisible group-hover:visible inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary whitespace-nowrap"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPage(1);
                        setSelectedModelIds([model._id]);
                      }}>
                      Only
                    </button>
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer">
              <FiActivity className="w-4 h-4 @[460px]:hidden" />
              <span className="@[460px]:block hidden">Status</span>
              {!allStatesSelected ? (
                <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none">
                  {selectedStates.length}
                </span>
              ) : (
                <FiChevronDown className="w-4 h-4 text-secondary-foreground/60 @[460px]:block hidden" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer"
              onClick={() => {
                setPage(1);
                setSelectedStates(STATE_GROUPS.map((g) => g.value));
              }}>
              Select all
            </button>
            <DropdownMenuSeparator />
            {STATE_GROUPS.map((g) => (
              <DropdownMenuCheckboxItem
                key={g.value}
                checked={selectedStates.includes(g.value)}
                onCheckedChange={(checked) => toggleState(g.value, checked)}
                onSelect={(e) => e.preventDefault()}
                className="group text-xs h-6">
                <span className="flex-1">{g.label}</span>
                <button
                  type="button"
                  className="ml-auto hidden group-hover:inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPage(1);
                    setSelectedStates([g.value]);
                  }}>
                  Only
                </button>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1 justify-end flex items-center gap-2">
          <span className="text-[11px] text-secondary-foreground/60 tabular-nums @[460px]:block hidden">{`${startIndex}-${endIndex} of ${total}`}</span>
          <div className="flex items-center border rounded-md h-8 overflow-hidden">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              title="First page"
              className="inline-flex h-8 px-1 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
              <FaAngleDoubleLeft className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              title="Previous page"
              className="inline-flex h-8 px-1 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
              <FaAngleLeft className="w-3 h-3" />
            </button>
            <div className="text-xs text-secondary-foreground tabular-nums flex items-center px-2">
              <span>{page}</span>
              <span>/{totalPages || 1}</span>
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              title="Next page"
              className="inline-flex h-8 px-1 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background">
              <FaAngleRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto relative h-full">
        {!loading && tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-2 text-secondary-foreground text-sm">
            <p>No tasks found.</p>
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card text-secondary-foreground">
              <TableRow>
                <TableHead className="p-1 h-auto text-secondary-foreground/70"></TableHead>
                <TableHead className="p-1 h-auto text-secondary-foreground/70 text-xs">
                  Model
                </TableHead>
                <TableHead className="p-1 h-auto text-secondary-foreground/70 text-xs">
                  Status
                </TableHead>
                <TableHead className="p-1 h-auto text-secondary-foreground/70 text-xs">
                  Prompt
                </TableHead>
                <TableHead className="p-1 h-auto text-secondary-foreground/70 text-xs">
                  Created
                </TableHead>
                <TableHead className="p-1 text-xs"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks
                .filter((t) => t && t.state)
                .map((task) => {
                  const isActive = [
                    "pending",
                    "in_progress",
                    "scheduled",
                  ].includes(task.state);
                  const canClear = [
                    "failed",
                    "scheduled",
                    "cancelled",
                  ].includes(task.state);
                  const prompt = task.request_body?.payload?.prompt;
                  const canRecall =
                    !!task.request_body?.payload &&
                    !!(task.model?._id || task.model);
                  return (
                    <TableRow
                      key={task._id}
                      className={`group cursor-pointer ${actioningId === task._id ? "opacity-30 animate-pulse pointer-events-none" : ""}`}
                      onClick={() =>
                        openModal(<TaskModal task={task} />, "side")
                      }>
                      <TableCell className="whitespace-nowrap p-1 px-2">
                        <StateBadge state={task.state} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-1">
                        <span className="text-[10px] py-0.5 text-secondary-foreground">
                          {task.model?.name || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="min-w-[100px] max-w-[180px] text-secondary-foreground/70 text-xs p-1">
                        <span className="line-clamp-2">
                          {task.status || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[240px] p-1">
                        <span className="text-secondary-foreground/80 text-xs line-clamp-2">
                          {prompt || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-secondary-foreground/70 text-xs p-1">
                        <span>
                          {dayjs(task.createdAt).format("MMM D, h:mm A")}
                        </span>
                      </TableCell>
                      <TableCell className="sticky right-0 whitespace-nowrap text-secondary-foreground/70 text-xs p-1 pr-2 group-hover:bg-[#1d1e23]">
                        <div className="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              onClick={(e) => e.stopPropagation()}>
                              <div className="p-1.5 rounded-md text-secondary-foreground/80 bg-background hover:bg-secondary hover:text-secondary-foreground">
                                <FiMoreHorizontal className="w-4 h-4" />
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              onClick={(e) => e.stopPropagation()}>
                              {canRecall && (
                                <DropdownMenuItem
                                  className="text-xs p-2"
                                  onClick={() => handleRecallSettings(task)}>
                                  <IoIosRefresh className="w-3 h-3" />
                                  Recall Settings
                                </DropdownMenuItem>
                              )}
                              {isActive && (
                                <DropdownMenuItem
                                  className="text-xs p-2"
                                  onClick={() => handleCancel(task)}>
                                  <TbCancel className="w-4 h-4" />
                                  Cancel
                                </DropdownMenuItem>
                              )}
                              {canClear && (
                                <DropdownMenuItem
                                  className="text-xs p-2 text-red-500 focus:text-primary-foreground group"
                                  onClick={() => handleDelete(task)}>
                                  <FiTrash2 className="w-4 h-4 text-red-500 group-hover:text-primary-foreground" />
                                  Clear
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
        {loading && <Loader />}
      </div>
    </aside>
  );
}
